import type { AnalyticsAttribution } from '@flowqa/shared';

const firstTouchStorageKey = 'flowqa.analytics.firstTouch';
const latestTouchStorageKey = 'flowqa.analytics.latestTouch';

const attributionParamNames = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
] as const;

const isAnalyticsAttribution = (value: unknown): value is AnalyticsAttribution => {
  if (!value || typeof value !== 'object') return false;
  return Object.values(value).every((entry) => entry === undefined || typeof entry === 'string');
};

const readStoredAttribution = (key: string): AnalyticsAttribution | null => {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.localStorage.getItem(key);
    if (!stored) return null;
    const parsed: unknown = JSON.parse(stored);
    return isAnalyticsAttribution(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const writeStoredAttribution = (key: string, value: AnalyticsAttribution): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Attribution capture is best-effort and must never block the app.
  }
};

const currentPage = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
};

const resolveAttributionFromLocation = (): AnalyticsAttribution | null => {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const captured: AnalyticsAttribution = {};
  attributionParamNames.forEach((name) => {
    const value = params.get(name);
    if (value && value.trim().length > 0) {
      captured[name] = value.trim();
    }
  });
  captured.landing_page = currentPage();
  if (document.referrer.trim().length > 0) {
    captured.referring_url = document.referrer;
  }
  const hasMarketingSignal =
    attributionParamNames.some((name) => captured[name] !== undefined) ||
    captured.referring_url !== undefined;
  return hasMarketingSignal ? captured : null;
};

export const captureAttributionFromCurrentVisit = (): void => {
  const attribution = resolveAttributionFromLocation();
  if (attribution === null) return;
  if (readStoredAttribution(firstTouchStorageKey) === null) {
    writeStoredAttribution(firstTouchStorageKey, attribution);
  }
  writeStoredAttribution(latestTouchStorageKey, attribution);
};

export const getStoredAnalyticsAttribution = (): {
  firstTouch: AnalyticsAttribution | null;
  latestTouch: AnalyticsAttribution | null;
} => ({
  firstTouch: readStoredAttribution(firstTouchStorageKey),
  latestTouch: readStoredAttribution(latestTouchStorageKey),
});
